import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import config from "../../config/config";
import CockpitShell from "../../components/CockpitShell";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

const navItems = [
  { href: "/dashboard/admin", label: "Command", icon: "🧠" },
  { href: "/dashboard/admin/users", label: "Users", icon: "👥" },
  { href: "/dashboard/security", label: "Security", icon: "🛡️" },
  { href: "/dashboard/systemstatus", label: "System", icon: "📡" },
];

const roles = ["admin", "employer", "applicant", "editor", "support", "press"];
const sortableColumns = ["email", "created_at", "last_login_at"];

function headers() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function openProtectedAdminVerificationDocument(referenceId) {
  const res = await axios.get(`${config.API_BASE_URL}/api/admin/verification-references/${referenceId}/document`, {
    headers: headers(),
    responseType: "blob",
  });
  const contentType = res.headers["content-type"] || "application/octet-stream";
  const blob = new Blob([res.data], { type: contentType });
  const objectUrl = window.URL.createObjectURL(blob);
  const popup = window.open(objectUrl, "_blank", "noopener,noreferrer");
  if (!popup) {
    const fallback = document.createElement("a");
    fallback.href = objectUrl;
    fallback.target = "_blank";
    fallback.rel = "noopener noreferrer";
    fallback.click();
  }
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
}

function relativeTime(dateValue) {
  if (!dateValue) return "-";
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return String(dateValue);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs} h ago`;
  const days = Math.floor(hrs / 24);
  return `${days} d ago`;
}

function prettifyToken(value) {
  const text = String(value || "").trim();
  if (!text) return "-";
  return text
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function displayValue(value) {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.map(displayValue).filter(Boolean).join(", ");
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "boolean") return value ? "Ja" : "Nein";
  if (typeof value === "object") return "";
  return String(value || "").trim();
}

function maybeFormatDate(value) {
  const text = displayValue(value);
  if (!text) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const parsed = new Date(`${text}T00:00:00Z`);
    if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleDateString("de-DE");
  }
  return text;
}

function readPath(source, path) {
  if (!source || typeof source !== "object") return undefined;
  return String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((acc, key) => (acc && typeof acc === "object" ? acc[key] : undefined), source);
}

function pickValue(source, paths = []) {
  for (const path of paths) {
    const value = readPath(source, path);
    const formatted = displayValue(value);
    if (formatted) return formatted;
  }
  return "";
}

function buildVerificationKeyFacts(reference) {
  const extracted = reference?.ai_extracted_json || reference?.ai_result?.extracted_json || {};
  const detectedType = String(reference?.ai_document_type_detected || reference?.ai_result?.document_type_detected || "").trim();
  const candidates = [
    { label: "Aussteller", value: pickValue(extracted, ["issuing_body", "issuer", "issuing_authority", "provider", "provider_name"]) },
    { label: "Institution", value: pickValue(extracted, ["institution_name", "institution", "school_name", "university_name"]) },
    { label: "Arbeitgeber", value: pickValue(extracted, ["employer_name", "company_name", "organization_name"]) },
    { label: "Abschluss / Zertifikat", value: pickValue(extracted, ["certificate_name", "qualification_name", "degree_name", "document_title", "title"]) },
    { label: "Sprache", value: pickValue(extracted, ["certificate_language", "language", "document_language"]) },
    { label: "CEFR-Level", value: pickValue(extracted, ["claimed_cefr_level_visible", "visible_cefr_level", "cefr_level", "claimed_cefr_level"]) },
    { label: "Pruefungsdatum", value: maybeFormatDate(pickValue(extracted, ["exam_date", "issued_at", "issue_date"])) },
    { label: "Referenznummer", value: pickValue(extracted, ["certificate_reference_no", "reference_no", "document_number", "certificate_number"]) },
    { label: "Position / Rolle", value: pickValue(extracted, ["position_title", "job_title", "role_title"]) },
    { label: "Zeitraum", value: pickValue(extracted, ["employment_period", "study_period", "period"]) },
    { label: "Datum", value: maybeFormatDate(pickValue(extracted, ["document_date", "date", "graduation_date"])) },
  ];

  const preferredLabelsByType = {
    language_certificate: ["Aussteller", "Sprache", "CEFR-Level", "Pruefungsdatum", "Referenznummer"],
    work_reference: ["Arbeitgeber", "Position / Rolle", "Zeitraum", "Datum", "Referenznummer"],
    employment_reference: ["Arbeitgeber", "Position / Rolle", "Zeitraum", "Datum", "Referenznummer"],
    education_certificate: ["Institution", "Abschluss / Zertifikat", "Datum", "Referenznummer"],
    degree_certificate: ["Institution", "Abschluss / Zertifikat", "Datum", "Referenznummer"],
    training_certificate: ["Institution", "Abschluss / Zertifikat", "Datum", "Referenznummer"],
    professional_certificate: ["Aussteller", "Abschluss / Zertifikat", "Datum", "Referenznummer"],
    license_document: ["Aussteller", "Abschluss / Zertifikat", "Datum", "Referenznummer"],
  };

  const preferredLabels = preferredLabelsByType[detectedType] || candidates.map((item) => item.label);
  return preferredLabels
    .map((label) => candidates.find((item) => item.label === label))
    .filter((item) => item && item.value)
    .slice(0, 6);
}

function buildVerificationFlags(reference) {
  const flags = reference?.ai_flags_json || reference?.ai_result?.flags_json || {};
  return [
    { label: "Lesbarkeit", value: displayValue(flags.readability) },
    { label: "Vollstaendigkeit", value: displayValue(flags.completeness) },
    { label: "Integritaet", value: displayValue(flags.integrity_assessment) },
  ].filter((item) => item.value);
}

function VerificationReferenceAuditCard({ row }) {
  const keyFacts = buildVerificationKeyFacts(row);
  const flagFacts = buildVerificationFlags(row);
  const decisionClass = row.ai_decision === "accepted"
    ? "border-emerald-300/35 bg-emerald-500/10 text-emerald-100"
    : row.ai_decision === "rejected"
      ? "border-rose-300/35 bg-rose-500/10 text-rose-100"
      : "border-white/15 bg-white/5 text-slate-200";
  const processingClass = row.ai_processing_status === "completed"
    ? "border-cyan-300/35 bg-cyan-500/10 text-cyan-100"
    : row.ai_processing_status === "failed"
      ? "border-rose-300/35 bg-rose-500/10 text-rose-100"
      : "border-amber-300/35 bg-amber-500/10 text-amber-100";
  const summaryText = displayValue(row.ai_summary_de);
  const translationText = displayValue(row.ai_translation_de);
  const readConfidence = row.ai_read_confidence === null || row.ai_read_confidence === undefined
    ? ""
    : `${Math.round(Number(row.ai_read_confidence) * 100)} %`;

  return (
    <Card className="card-e4e">
      <CardContent className="space-y-4 pt-4 text-sm text-slate-200">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Verification Audit</p>
            <p className="font-medium text-white">{row.title || prettifyToken(row.type)}</p>
            <p className="text-xs text-slate-400">
              Ref #{row.id} · {prettifyToken(row.type)} · {row.created_at ? new Date(row.created_at).toLocaleString() : "-"}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className={decisionClass}>{row.ai_decision ? prettifyToken(row.ai_decision) : "Keine Entscheidung"}</Badge>
            <Badge variant="outline" className={processingClass}>{prettifyToken(row.ai_processing_status || "unknown")}</Badge>
            <Badge variant="outline" className="border-white/20 bg-white/5 text-slate-200">{prettifyToken(row.status)}</Badge>
            {row.has_file ? (
              <Button
                variant="outline"
                className="border-cyan-300/35 bg-cyan-500/10 text-cyan-100 hover:bg-cyan-400/20"
                onClick={() => openProtectedAdminVerificationDocument(row.id)}
              >
                Dokument ansehen
              </Button>
            ) : null}
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <div className="rounded-lg border border-white/10 bg-slate-900/40 p-3">
            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Erkannter Typ</p>
            <p className="mt-1 text-slate-100">{prettifyToken(row.ai_document_type_detected)}</p>
          </div>
          <div className="rounded-lg border border-white/10 bg-slate-900/40 p-3">
            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Dokumentenfamilie</p>
            <p className="mt-1 text-slate-100">{prettifyToken(row.ai_document_family)}</p>
          </div>
          <div className="rounded-lg border border-white/10 bg-slate-900/40 p-3">
            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Quellsprache</p>
            <p className="mt-1 text-slate-100">{displayValue(row.ai_source_language) || "-"}</p>
          </div>
          <div className="rounded-lg border border-white/10 bg-slate-900/40 p-3">
            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Lesestatus</p>
            <p className="mt-1 text-slate-100">{prettifyToken(row.ai_read_status)}</p>
          </div>
          {readConfidence ? (
            <div className="rounded-lg border border-white/10 bg-slate-900/40 p-3">
              <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Lesekonfidenz</p>
              <p className="mt-1 text-slate-100">{readConfidence}</p>
            </div>
          ) : null}
          {row.ai_processing_error ? (
            <div className="rounded-lg border border-rose-300/25 bg-rose-500/10 p-3">
              <p className="text-xs uppercase tracking-[0.18em] text-rose-200">Processing Error</p>
              <p className="mt-1 text-rose-100">{row.ai_processing_error}</p>
            </div>
          ) : null}
        </div>

        {row.ai_decision === "rejected" ? (
          <div className="rounded-xl border border-rose-300/25 bg-rose-500/10 p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-rose-200">Automatische Ablehnung</p>
            <div className="mt-2 space-y-2 text-rose-100">
              {row.ai_reason_code ? <p><strong>{row.ai_reason_code}</strong></p> : null}
              {row.ai_reason_text_de ? <p>{row.ai_reason_text_de}</p> : null}
              {row.ai_reupload_hint_de ? <p>{row.ai_reupload_hint_de}</p> : null}
            </div>
          </div>
        ) : null}

        {summaryText ? (
          <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Summary</p>
            <p className="mt-2 whitespace-pre-wrap text-slate-100">{summaryText}</p>
          </div>
        ) : null}

        {keyFacts.length ? (
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Kerndaten</p>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {keyFacts.map((fact) => (
                <div key={fact.label} className="rounded-lg border border-white/10 bg-slate-900/40 p-3">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">{fact.label}</p>
                  <p className="mt-1 text-slate-100">{fact.value}</p>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {flagFacts.length ? (
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Pruefhinweise</p>
            <div className="grid gap-3 md:grid-cols-3">
              {flagFacts.map((fact) => (
                <div key={fact.label} className="rounded-lg border border-white/10 bg-slate-900/40 p-3">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">{fact.label}</p>
                  <p className="mt-1 text-slate-100">{fact.value}</p>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {translationText ? (
          <div className="rounded-xl border border-cyan-300/20 bg-cyan-500/5 p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-cyan-200">KI-Uebersetzung</p>
            <p className="mt-2 text-xs text-slate-300">Das Originaldokument bleibt fuehrend. Die Uebersetzung dient nur der schnelleren operativen Einordnung.</p>
            <div className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap rounded-lg border border-white/10 bg-slate-950/40 p-3 text-slate-100">
              {translationText}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

const UserManagement = () => {
  const [queryInput, setQueryInput] = useState("");
  const [query, setQuery] = useState("");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const [verified, setVerified] = useState("all");
  const [locked, setLocked] = useState("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [data, setData] = useState({ total: 0, users: [] });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const [visibleCols, setVisibleCols] = useState({
    userid: true,
    email: true,
    role: true,
    status: true,
    verified: true,
    last_login: true,
    created_at: true,
    actions: true,
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkRole, setBulkRole] = useState("");
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [auditRows, setAuditRows] = useState([]);
  const [verificationRows, setVerificationRows] = useState([]);
  const [resetToken, setResetToken] = useState("");
  const [verifyToken, setVerifyToken] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ email: "", role: "applicant", initial_password: "", invite_only: false });

  useEffect(() => {
    document.title = "Experts4Europe | User Management";
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setQuery(queryInput.trim());
    }, 380);
    return () => clearTimeout(t);
  }, [queryInput]);

  useEffect(() => {
    setPage(1);
  }, [role, status, verified, locked, sortBy, sortDir, limit]);

  const filters = useMemo(
    () => ({
      search: query || undefined,
      role: role === "all" ? undefined : role,
      status: status === "all" ? undefined : status,
      verified: verified === "all" ? undefined : verified,
      locked: locked === "all" ? undefined : locked,
      sort_by: sortBy,
      sort_dir: sortDir,
      page,
      limit,
    }),
    [query, role, status, verified, locked, sortBy, sortDir, page, limit]
  );

  const loadUsers = async () => {
    setLoading(true);
    setMessage("");
    try {
      const res = await axios.get(`${config.API_BASE_URL}/api/admin/users`, { params: filters, headers: headers() });
      setData({ total: res.data.total || 0, users: res.data.users || [] });
    } catch (e) {
      setMessage(e.response?.data?.message || "Could not load users.");
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${config.API_BASE_URL}/api/admin/users/export`, {
        params: filters,
        headers: headers(),
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "experts4europe_users.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setMessage(e.response?.data?.message || "CSV export failed.");
    }
  };

  const openDetail = async (user) => {
    setSelected(user);
    setDetailOpen(true);
    setResetToken("");
    setVerifyToken("");
    setVerificationRows([]);
    try {
      const [u, s, a, v] = await Promise.all([
        axios.get(`${config.API_BASE_URL}/api/admin/users/${user.userid}`, { headers: headers() }),
        axios.get(`${config.API_BASE_URL}/api/admin/users/${user.userid}/sessions`, { headers: headers() }),
        axios.get(`${config.API_BASE_URL}/api/admin/users/${user.userid}/audit`, { headers: headers() }),
        axios.get(`${config.API_BASE_URL}/api/admin/verification-references`, {
          headers: headers(),
          params: { userid: user.userid, limit: 25 },
        }),
      ]);
      setDetail(u.data);
      setSessions(s.data.sessions || []);
      setAuditRows(a.data.rows || []);
      setVerificationRows(v.data?.rows || []);
    } catch (e) {
      setMessage(e.response?.data?.message || "Could not load user detail.");
    }
  };

  const patchUser = async (userid, payload) => {
    await axios.patch(`${config.API_BASE_URL}/api/admin/users/${userid}`, payload, { headers: headers() });
    await loadUsers();
    if (selected?.userid === userid) await openDetail({ userid });
  };

  useEffect(() => {
    loadUsers();
  }, [filters]);

  useEffect(() => {
    setSelectedIds([]);
  }, [data.users]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / limit));
  const allVisibleSelected = data.users.length > 0 && data.users.every((u) => selectedIds.includes(u.userid));

  const toggleSort = (col) => {
    if (!sortableColumns.includes(col)) return;
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(col);
    setSortDir("asc");
  };

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(data.users.map((u) => u.userid));
      return;
    }
    setSelectedIds([]);
  };

  const toggleSelect = (userid, checked) => {
    if (checked) {
      setSelectedIds((prev) => (prev.includes(userid) ? prev : [...prev, userid]));
      return;
    }
    setSelectedIds((prev) => prev.filter((id) => id !== userid));
  };

  const runBulkAction = async (action) => {
    if (!selectedIds.length) return;
    try {
      const normalized = action === "logout" ? "force_logout" : action;
      await axios.post(`${config.API_BASE_URL}/api/admin/users/bulk`, { action: normalized, userids: selectedIds }, { headers: headers() });
      setMessage(`Bulk action "${action}" completed for ${selectedIds.length} users.`);
      await loadUsers();
    } catch (e) {
      setMessage(e.response?.data?.message || "Bulk action failed.");
    }
  };

  const runBulkRole = async (nextRole) => {
    if (!selectedIds.length || !nextRole) return;
    try {
      await axios.post(`${config.API_BASE_URL}/api/admin/users/bulk`, { action: "set_role", role: nextRole, userids: selectedIds }, { headers: headers() });
      setMessage(`Role "${nextRole}" applied to ${selectedIds.length} users.`);
      await loadUsers();
    } catch (e) {
      setMessage(e.response?.data?.message || "Bulk role change failed.");
    }
  };

  const softRefreshDetail = async () => {
    if (!selected?.userid) return;
    await openDetail({ userid: selected.userid });
  };

  return (
    <CockpitShell
      title="User Management"
      subtitle="Admin cockpit for full user lifecycle, sessions, audit and security actions."
      badge="Experts4Europe Admin"
      navItems={navItems}
      actions={
        <>
          <Button aria-label="Export user list as CSV" variant="outline" className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10" onClick={exportCsv}>
            Export CSV
          </Button>
          <Button aria-label="Open create or invite user dialog" className="bg-cyan-500 text-slate-950 hover:bg-cyan-300" onClick={() => setCreateOpen(true)}>
            Create / Invite User
          </Button>
        </>
      }
    >
      <Card className="card-e4e">
        <CardHeader>
          <CardTitle className="font-display text-white">Search & Filter</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-8">
          <Input aria-label="Search users by userid or email" value={queryInput} onChange={(e) => setQueryInput(e.target.value)} placeholder="Search userid/email..." className="border-white/20 bg-slate-950/40 text-white placeholder:text-slate-400 xl:col-span-2" />
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="border-white/20 bg-slate-950/40 text-white"><SelectValue placeholder="Role" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="border-white/20 bg-slate-950/40 text-white"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              <SelectItem value="active">active</SelectItem>
              <SelectItem value="inactive">inactive</SelectItem>
            </SelectContent>
          </Select>
          <Select value={verified} onValueChange={setVerified}>
            <SelectTrigger className="border-white/20 bg-slate-950/40 text-white"><SelectValue placeholder="Verified" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All verified</SelectItem>
              <SelectItem value="true">verified</SelectItem>
              <SelectItem value="false">unverified</SelectItem>
            </SelectContent>
          </Select>
          <Select value={locked} onValueChange={setLocked}>
            <SelectTrigger className="border-white/20 bg-slate-950/40 text-white"><SelectValue placeholder="Locked" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All lock states</SelectItem>
              <SelectItem value="true">locked</SelectItem>
              <SelectItem value="false">unlocked</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="border-white/20 bg-slate-950/40 text-white"><SelectValue placeholder="Sort by" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at">created_at</SelectItem>
              <SelectItem value="email">email</SelectItem>
              <SelectItem value="last_login_at">last_login_at</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortDir} onValueChange={setSortDir}>
            <SelectTrigger className="border-white/20 bg-slate-950/40 text-white"><SelectValue placeholder="Sort dir" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="asc">asc</SelectItem>
              <SelectItem value="desc">desc</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10" onClick={() => {
            setQueryInput("");
            setQuery("");
            setRole("all");
            setStatus("all");
            setVerified("all");
            setLocked("all");
            setSortBy("created_at");
            setSortDir("desc");
            setPage(1);
            setLimit(20);
            setSelectedIds([]);
          }}>
            Clear filters
          </Button>
          <Button aria-label="Toggle column visibility options" variant="outline" className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10" onClick={() => setColumnPickerOpen((v) => !v)}>
            Columns
          </Button>
        </CardContent>
      </Card>

      {columnPickerOpen ? (
        <Card className="card-e4e">
          <CardHeader><CardTitle className="font-display text-white text-sm">Visible Columns</CardTitle></CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-4">
            {Object.keys(visibleCols).map((k) => (
              <label key={k} className="flex items-center gap-2 text-sm text-slate-200">
                <Checkbox checked={visibleCols[k]} onCheckedChange={(v) => setVisibleCols((prev) => ({ ...prev, [k]: Boolean(v) }))} />
                {k}
              </label>
            ))}
          </CardContent>
        </Card>
      ) : null}

      {message ? <p className="rounded-lg border border-rose-400/30 bg-rose-500/10 p-3 text-sm text-rose-200">{message}</p> : null}

      <Card className="card-e4e">
        <CardHeader>
          <CardTitle className="font-display text-white">Bulk Actions</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2 md:grid-cols-4">
          <div className="rounded-lg border border-white/10 bg-slate-900/40 px-3 py-2 text-sm text-slate-200 md:col-span-4">
            Selected: <strong className="text-white">{selectedIds.length}</strong> users
          </div>
          <Button aria-label="Activate selected users" variant="outline" className="border-emerald-300/30 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-400/20" disabled={!selectedIds.length} onClick={() => runBulkAction("activate")}>Activate</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button aria-label="Deactivate selected users" variant="outline" className="border-amber-300/30 bg-amber-500/10 text-amber-100 hover:bg-amber-400/20" disabled={!selectedIds.length}>Deactivate</Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="border-white/15 bg-slate-950 text-slate-100">
              <AlertDialogHeader><AlertDialogTitle>Deactivate selected users?</AlertDialogTitle><AlertDialogDescription>This will disable login for all selected users.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => runBulkAction("deactivate")}>Confirm</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button aria-label="Force logout for selected users" variant="outline" className="border-cyan-300/30 bg-cyan-500/10 text-cyan-100 hover:bg-cyan-400/20" disabled={!selectedIds.length}>Force Logout</Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="border-white/15 bg-slate-950 text-slate-100">
              <AlertDialogHeader><AlertDialogTitle>Force logout selected users?</AlertDialogTitle><AlertDialogDescription>All active sessions of selected users will be revoked.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => runBulkAction("logout")}>Confirm</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Select value={bulkRole} onValueChange={setBulkRole}>
            <SelectTrigger className="border-white/20 bg-slate-950/40 text-white" disabled={!selectedIds.length}>
              <SelectValue placeholder="Bulk role..." />
            </SelectTrigger>
            <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10" disabled={!selectedIds.length || !bulkRole} onClick={() => runBulkRole(bulkRole)}>Apply Role</Button>
        </CardContent>
      </Card>

      <Card className="card-e4e">
        <CardHeader>
          <CardTitle className="font-display text-white">Users ({data.total || 0})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="overflow-auto rounded-xl border border-white/10">
            <table className="w-full min-w-[1060px] text-sm">
              <thead className="sticky top-0 z-10 bg-slate-950/95 text-slate-300 backdrop-blur-sm">
                <tr>
                  <th className="px-3 py-2 text-left">
                    <Checkbox aria-label="Select all rows" checked={allVisibleSelected} onCheckedChange={toggleSelectAll} />
                  </th>
                  {visibleCols.userid ? <th className="px-3 py-2 text-left">UserID</th> : null}
                  {visibleCols.email ? (
                    <th className="px-3 py-2 text-left">
                      <button type="button" className="e4e-focus-ring rounded px-1 py-0.5 hover:text-white" onClick={() => toggleSort("email")}>Email {sortBy === "email" ? (sortDir === "asc" ? "↑" : "↓") : ""}</button>
                    </th>
                  ) : null}
                  {visibleCols.role ? <th className="px-3 py-2 text-left">Role</th> : null}
                  {visibleCols.status ? <th className="px-3 py-2 text-left">Status</th> : null}
                  {visibleCols.verified ? <th className="px-3 py-2 text-left">Verified</th> : null}
                  {visibleCols.last_login ? (
                    <th className="px-3 py-2 text-left">
                      <button type="button" className="e4e-focus-ring rounded px-1 py-0.5 hover:text-white" onClick={() => toggleSort("last_login_at")}>Last Login {sortBy === "last_login_at" ? (sortDir === "asc" ? "↑" : "↓") : ""}</button>
                    </th>
                  ) : null}
                  {visibleCols.created_at ? (
                    <th className="px-3 py-2 text-left">
                      <button type="button" className="e4e-focus-ring rounded px-1 py-0.5 hover:text-white" onClick={() => toggleSort("created_at")}>Created {sortBy === "created_at" ? (sortDir === "asc" ? "↑" : "↓") : ""}</button>
                    </th>
                  ) : null}
                  {visibleCols.actions ? <th className="px-3 py-2 text-left">Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(8)].map((_, i) => (
                    <tr key={`sk_${i}`} className="border-t border-white/10">
                      <td className="px-3 py-3"><div className="h-5 w-5 animate-pulse rounded bg-white/10" /></td>
                      <td className="px-3 py-3" colSpan={visibleCols.userid ? 1 : 0}><div className="h-4 w-20 animate-pulse rounded bg-white/10" /></td>
                      <td className="px-3 py-3" colSpan={visibleCols.email ? 1 : 0}><div className="h-4 w-44 animate-pulse rounded bg-white/10" /></td>
                      <td className="px-3 py-3" colSpan={visibleCols.role ? 1 : 0}><div className="h-4 w-20 animate-pulse rounded bg-white/10" /></td>
                      <td className="px-3 py-3" colSpan={visibleCols.status ? 1 : 0}><div className="h-4 w-16 animate-pulse rounded bg-white/10" /></td>
                      <td className="px-3 py-3" colSpan={visibleCols.verified ? 1 : 0}><div className="h-4 w-10 animate-pulse rounded bg-white/10" /></td>
                      <td className="px-3 py-3" colSpan={visibleCols.last_login ? 1 : 0}><div className="h-4 w-32 animate-pulse rounded bg-white/10" /></td>
                      <td className="px-3 py-3" colSpan={visibleCols.created_at ? 1 : 0}><div className="h-4 w-32 animate-pulse rounded bg-white/10" /></td>
                      <td className="px-3 py-3" colSpan={visibleCols.actions ? 1 : 0}><div className="h-4 w-24 animate-pulse rounded bg-white/10" /></td>
                    </tr>
                  ))
                ) : data.users.length === 0 ? (
                  <tr>
                    <td className="px-6 py-8 text-center text-slate-300" colSpan={9}>
                      <p className="font-medium text-slate-100">No users match the current filters.</p>
                      <p className="mt-1 text-xs text-slate-400">Adjust filters or create a new user to get started.</p>
                      <Button className="mt-3 bg-cyan-500 text-slate-950 hover:bg-cyan-300" onClick={() => setCreateOpen(true)}>
                        Create User
                      </Button>
                    </td>
                  </tr>
                ) : data.users.map((u) => (
                  <tr key={u.userid} className="cursor-pointer border-t border-white/10 hover:bg-white/5" onClick={() => openDetail(u)}>
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <Checkbox aria-label={`Select ${u.userid}`} checked={selectedIds.includes(u.userid)} onCheckedChange={(v) => toggleSelect(u.userid, Boolean(v))} />
                    </td>
                    {visibleCols.userid ? <td className="px-3 py-2 text-slate-100">{u.userid}</td> : null}
                    {visibleCols.email ? <td className="px-3 py-2 text-slate-200">{u.email}</td> : null}
                    {visibleCols.role ? <td className="px-3 py-2"><Badge variant="outline" className="border-cyan-300/35 bg-cyan-500/10 text-cyan-100">{u.role}</Badge></td> : null}
                    {visibleCols.status ? <td className="px-3 py-2">{u.is_active ? <Badge className="bg-emerald-500/15 text-emerald-100" variant="outline">active</Badge> : <Badge className="bg-rose-500/15 text-rose-100" variant="outline">inactive</Badge>}</td> : null}
                    {visibleCols.verified ? <td className="px-3 py-2">{u.email_verified ? "yes" : "no"}</td> : null}
                    {visibleCols.last_login ? <td className="px-3 py-2 text-slate-300">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "-"}</td> : null}
                    {visibleCols.created_at ? <td className="px-3 py-2 text-slate-300">{u.created_at ? new Date(u.created_at).toLocaleString() : "-"}</td> : null}
                    {visibleCols.actions ? (
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="outline" className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10">...</Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openDetail(u)}>Open details</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => patchUser(u.userid, { is_active: u.is_active ? 0 : 1 })}>
                              {u.is_active ? "Deactivate" : "Activate"}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={async () => {
                              await axios.post(`${config.API_BASE_URL}/api/admin/users/${u.userid}/force-logout`, {}, { headers: headers() });
                              await loadUsers();
                            }}>
                              Force logout
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between gap-2 text-sm text-slate-300">
            <span>Page {page} / {totalPages}</span>
            <div className="flex gap-2">
              <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
                <SelectTrigger className="h-9 w-[110px] border-white/20 bg-slate-950/40 text-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 / page</SelectItem>
                  <SelectItem value="20">20 / page</SelectItem>
                  <SelectItem value="50">50 / page</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
              <Button size="sm" variant="outline" className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-h-[90vh] overflow-auto border-white/15 bg-slate-950 text-slate-100 sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">User Detail: {selected?.userid}</DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="bg-white/5">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="verification">Verification</TabsTrigger>
              <TabsTrigger value="security">Security</TabsTrigger>
              <TabsTrigger value="sessions">Sessions</TabsTrigger>
              <TabsTrigger value="audit">Audit</TabsTrigger>
              <TabsTrigger value="danger">Danger</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-3">
              <Card className="card-e4e">
                <CardContent className="space-y-3 pt-4 text-sm">
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="border-cyan-300/35 bg-cyan-500/10 text-cyan-100">{detail?.role || "-"}</Badge>
                    <Badge variant="outline" className={detail?.is_active ? "border-emerald-300/35 bg-emerald-500/10 text-emerald-100" : "border-rose-300/35 bg-rose-500/10 text-rose-100"}>
                      {detail?.is_active ? "active" : "inactive"}
                    </Badge>
                    <Badge variant="outline" className={detail?.email_verified ? "border-emerald-300/35 bg-emerald-500/10 text-emerald-100" : "border-amber-300/35 bg-amber-500/10 text-amber-100"}>
                      {detail?.email_verified ? "verified" : "unverified"}
                    </Badge>
                    <Badge variant="outline" className={detail?.locked_until ? "border-rose-300/35 bg-rose-500/10 text-rose-100" : "border-white/20 bg-white/5 text-slate-200"}>
                      {detail?.locked_until ? "locked" : "unlocked"}
                    </Badge>
                  </div>
                  <p>UserID: <strong>{detail?.userid || selected?.userid || "-"}</strong></p>
                  <p>Email: <strong>{detail?.email || "-"}</strong></p>
                  <p>Last Login: <strong>{detail?.last_login_at ? new Date(detail.last_login_at).toLocaleString() : "-"}</strong></p>
                  <p>Created: <strong>{detail?.created_at ? new Date(detail.created_at).toLocaleString() : "-"}</strong></p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="verification" className="space-y-3">
              <Card className="card-e4e">
                <CardContent className="space-y-2 pt-4 text-sm text-slate-300">
                  <p className="font-medium text-slate-100">Verification bleibt hier audit-only.</p>
                  <p>Keine manuelle Freigabe, keine Ablehnungsaktion und keine Queue. Sichtbar sind nur AI-Entscheidung, Summary, Uebersetzung, Ablehnungsgruende und extrahierte Kerndaten.</p>
                </CardContent>
              </Card>
              {verificationRows.length === 0 ? (
                <Card className="card-e4e">
                  <CardContent className="pt-4 text-sm text-slate-300">
                    Fuer diesen User liegen aktuell keine Verification References vor.
                  </CardContent>
                </Card>
              ) : verificationRows.map((row) => (
                <VerificationReferenceAuditCard key={row.id} row={row} />
              ))}
            </TabsContent>

            <TabsContent value="security" className="space-y-3">
              <Card className="card-e4e">
                <CardContent className="grid gap-2 pt-4 md:grid-cols-2">
                  <div className="rounded-lg border border-white/10 bg-slate-900/40 p-3 text-xs text-slate-300 md:col-span-2">
                    Failed logins: <strong className="text-white">{detail?.failed_login_count ?? 0}</strong> | Locked until:{" "}
                    <strong className="text-white">{detail?.locked_until ? new Date(detail.locked_until).toLocaleString() : "-"}</strong>
                  </div>
                  <Button variant="outline" className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10" onClick={async () => { await patchUser(selected.userid, { email_verified: detail?.email_verified ? 0 : 1 }); }}>
                    {detail?.email_verified ? "Mark Unverified" : "Mark Verified"}
                  </Button>
                  <Select value={detail?.role || "applicant"} onValueChange={async (v) => { await patchUser(selected.userid, { role: v }); }}>
                    <SelectTrigger className="border-white/20 bg-slate-950/40 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" className="border-amber-300/30 bg-amber-500/10 text-amber-100 hover:bg-amber-400/20">Lock User</Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="border-white/15 bg-slate-950 text-slate-100">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Lock this user?</AlertDialogTitle>
                        <AlertDialogDescription>This will block login until unlocked manually or lock timeout expires.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={async () => { await axios.post(`${config.API_BASE_URL}/api/admin/users/${selected.userid}/lock`, {}, { headers: headers() }); await softRefreshDetail(); }}>Confirm lock</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" className="border-emerald-300/30 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-400/20">Unlock User</Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="border-white/15 bg-slate-950 text-slate-100">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Unlock this user?</AlertDialogTitle>
                        <AlertDialogDescription>Failed login counter and lock status will be reset.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={async () => { await axios.post(`${config.API_BASE_URL}/api/admin/users/${selected.userid}/unlock`, {}, { headers: headers() }); await softRefreshDetail(); }}>Confirm unlock</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <Button variant="outline" className="border-cyan-300/30 bg-cyan-500/10 text-cyan-100 hover:bg-cyan-400/20" onClick={async () => {
                    const res = await axios.post(`${config.API_BASE_URL}/api/admin/users/${selected.userid}/reset-password`, {}, { headers: headers() });
                    setResetToken(res.data?.dev_reset_token || "");
                  }}>
                    Generate Reset Token
                  </Button>
                  <Button variant="outline" className="border-cyan-300/30 bg-cyan-500/10 text-cyan-100 hover:bg-cyan-400/20" onClick={async () => {
                    const res = await axios.post(`${config.API_BASE_URL}/api/admin/users/${selected.userid}/verify-email`, { mode: "token" }, { headers: headers() });
                    setVerifyToken(res.data?.dev_verify_token || "");
                  }}>
                    Generate Verify Token
                  </Button>
                </CardContent>
              </Card>
              {resetToken ? <p className="rounded-lg border border-cyan-400/30 bg-cyan-500/10 p-2 text-xs text-cyan-100">DEV reset token: {resetToken}</p> : null}
              {verifyToken ? <p className="rounded-lg border border-cyan-400/30 bg-cyan-500/10 p-2 text-xs text-cyan-100">DEV verify token: {verifyToken}</p> : null}
            </TabsContent>

            <TabsContent value="sessions" className="space-y-2">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10">Force Logout (revoke all)</Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="border-white/15 bg-slate-950 text-slate-100">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Revoke all sessions?</AlertDialogTitle>
                    <AlertDialogDescription>The user will be logged out from all devices immediately.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={async () => { await axios.post(`${config.API_BASE_URL}/api/admin/users/${selected.userid}/force-logout`, {}, { headers: headers() }); await softRefreshDetail(); }}>Revoke all</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              {sessions.map((s) => (
                <Card key={s.id} className="card-e4e">
                  <CardContent className="flex flex-wrap items-center justify-between gap-2 pt-4 text-sm">
                    <div>
                      <p>ID: {s.id} {s.revoked_at ? "(revoked)" : "(active)"}</p>
                      <p className="text-slate-300">{s.ip || "-"} · {s.user_agent || "-"}</p>
                      <p className="text-slate-400">
                        Created: {s.created_at ? new Date(s.created_at).toLocaleString() : "-"} · Last seen: {s.last_seen_at ? `${new Date(s.last_seen_at).toLocaleString()} (${relativeTime(s.last_seen_at)})` : "-"}
                      </p>
                    </div>
                    {!s.revoked_at ? (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" className="border-rose-300/30 bg-rose-500/10 text-rose-100 hover:bg-rose-400/20">Revoke</Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="border-white/15 bg-slate-950 text-slate-100">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Revoke session #{s.id}?</AlertDialogTitle>
                            <AlertDialogDescription>This session token will be invalid immediately.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={async () => {
                              await axios.delete(`${config.API_BASE_URL}/api/admin/users/${selected.userid}/sessions/${s.id}`, { headers: headers() });
                              await softRefreshDetail();
                            }}>Revoke session</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    ) : null}
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            <TabsContent value="audit" className="space-y-2">
              {auditRows.map((a) => (
                <Card key={a.id} className="card-e4e">
                  <CardContent className="pt-4 text-sm text-slate-200">
                    <p><strong>{a.action}</strong> · {new Date(a.created_at).toLocaleString()} ({relativeTime(a.created_at)})</p>
                    <p className="text-slate-300">Actor: {a.actor_userid || "-"} | Target: {a.target_userid || "-"}</p>
                    <p className="text-slate-300">{a.details_json || "-"}</p>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            <TabsContent value="danger" className="space-y-2">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="border-amber-300/40 bg-amber-500/10 text-amber-100 hover:bg-amber-400/20">Anonymize User</Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="border-white/15 bg-slate-950 text-slate-100">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Anonymize this user?</AlertDialogTitle>
                    <AlertDialogDescription>This action removes personal identifiers but keeps technical audit links.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={async () => {
                      await axios.delete(`${config.API_BASE_URL}/api/admin/users/${selected.userid}?mode=anonymize`, { headers: headers() });
                      setDetailOpen(false);
                      await loadUsers();
                    }}>Confirm anonymize</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="border-rose-300/40 bg-rose-500/10 text-rose-100 hover:bg-rose-400/20">Delete User</Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="border-white/15 bg-slate-950 text-slate-100">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this user permanently?</AlertDialogTitle>
                    <AlertDialogDescription>This is irreversible and should only be used if anonymize is not sufficient.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={async () => {
                      await axios.delete(`${config.API_BASE_URL}/api/admin/users/${selected.userid}`, { headers: headers() });
                      setDetailOpen(false);
                      await loadUsers();
                    }}>Delete permanently</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="border-white/15 bg-slate-950 text-slate-100 sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Create / Invite User</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Email" value={createForm.email} onChange={(e) => setCreateForm((p) => ({ ...p, email: e.target.value }))} className="border-white/20 bg-slate-950/40 text-white placeholder:text-slate-400" />
            <Select value={createForm.role} onValueChange={(v) => setCreateForm((p) => ({ ...p, role: v }))}>
              <SelectTrigger className="border-white/20 bg-slate-950/40 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input type="checkbox" checked={createForm.invite_only} onChange={(e) => setCreateForm((p) => ({ ...p, invite_only: e.target.checked }))} />
              Invite only (no initial password required)
            </label>
            {!createForm.invite_only ? (
              <Input placeholder="Initial Password (min 8)" value={createForm.initial_password} onChange={(e) => setCreateForm((p) => ({ ...p, initial_password: e.target.value }))} className="border-white/20 bg-slate-950/40 text-white placeholder:text-slate-400" />
            ) : null}
            <Button className="w-full bg-cyan-500 text-slate-950 hover:bg-cyan-300" onClick={async () => {
              const res = await axios.post(`${config.API_BASE_URL}/api/admin/users`, createForm, { headers: headers() });
              if (res.data?.dev_invite_token) setMessage(`DEV invite token for ${res.data.user.userid}: ${res.data.dev_invite_token}`);
              setCreateOpen(false);
              setCreateForm({ email: "", role: "applicant", initial_password: "", invite_only: false });
              await loadUsers();
            }}>
              Create User
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </CockpitShell>
  );
};

export default UserManagement;
